"use client"

import { useEffect, useState } from "react"
import { NavLink, useLocation } from "react-router-dom"
import { Box, Flex, Text, Tooltip, Icon, IconButton, Image } from "@chakra-ui/react"
import {
  FaChartBar,
  FaLightbulb,
  FaBullseye,
  FaLayerGroup,
  FaPercent,
  FaClipboardList,
  FaUsers,
  FaBoxOpen,
  FaStore,
  FaFileUpload,
  FaExchangeAlt,
  FaTrashAlt,
  FaClipboardCheck,
  FaTasks,
  FaMapMarkedAlt,
  FaServer,
  FaCheckDouble,
  FaFileAlt,
  FaBell,
  FaUser,
  FaHistory,
  FaUserTie,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
} from "react-icons/fa"

import SupplierNavigation from "../SupplierNavigation"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"
import { neutral, brand, type, radius, layout, motion } from "../../theme/tokens"
import { resolveBrandMark } from "../../tools/brandMark"
import { analyseMark } from "../../tools/markTrim"

// Nav is data, not markup. The previous layout repeated the same 12-line
// <Link><Box><Icon><Text> block seventeen times, which is why icons had drifted
// (Sales and Margin shared one, five admin pages shared FaFileImport).
const ANALYTICS = [
  { to: "/sales", label: "Sales", icon: FaChartBar, page: "Sales", restrictable: true },
  { to: "/insights", label: "Insights", icon: FaLightbulb, page: "Insights", restrictable: true },
  // retailers: greyed out (not clickable) for any other selected retailer — only
  // PnP delivers strike-rate data (chk/dis/cli/mak_strikerate are empty on prod, 2026-10-02).
  { to: "/strike", label: "Strike Rate", icon: FaBullseye, page: "StrikeRate", restrictable: true, retailers: ["PNP"], retailersNote: "Pick n Pay only" },
  { to: "/ranging", label: "Ranging", icon: FaLayerGroup, page: "Ranging", restrictable: true },
  { to: "/margin", label: "Margin", icon: FaPercent, page: "Margin", restrictable: true },
  { to: "/orders", label: "Orders", icon: FaClipboardList, adminOnly: true },
]

// Field section (2026-10-06, docs/michael/rep-field/PLAN-rep-field.md): store work done in the
// field. masterAlso: the two masters see it without a page grant (the API admits them too).
const FIELD = [
  // suppliers + retailers: greyed out unless the top bar shows one of each (Michael 2026-10-06:
  // the scorecard is Curaden on Dis-Chem only).
  // Rep tracker (Blue Mango's Georep replacement, 2026-10-06): admins of an organisation with reps (repPage —
  // the same rule as the Rep page); My route: a rep login (RepRoute page grant).
  // Rep Sales (was "Rep" under Analytics; moved + renamed 2026-10-07, Michael — "Rep Sales, Rep Routing,
  // Scorecard, then we have it all together"). repPage: ORGANISATION-scoped, not page-granted — shown when the
  // organisation in view (the master's org toggle, or the caller's own) has rep→store assignments
  // (docs/michael/blue-mango-rep-report/TASK-rep-report.md, 2026-09-21).
  // Rep Routing (was "Rep tracker", renamed 2026-10-07). Released on prod 2026-10-07: the masters (edit) and the
  // organisation's admin (Linda — VIEW only, decided server-side) of an organisation with reps.
  // 2026-10-12 (Helena): the two are one screen — "Rep Sales & Routing" at /rep (pages/rep/RepHub.js), shown to
  // anyone who could see either; /field/reps redirects there. anyOf = the two old items' rules.
  {
    to: "/rep", label: "Rep Sales & Routing", icon: FaUserTie,
    anyOf: [
      { repPage: true, restrictable: true },
      { repPage: true, mastersOrOrgAdmin: true, camPage: "RepRouting" },
    ],
  },
  { to: "/field/route", label: "My route", icon: FaMapMarkedAlt, page: "RepRoute" },
  { to: "/scorecard", label: "Scorecard", icon: FaTasks, page: "Scorecard", masterAlso: true, suppliers: ["PRIME DENTAL"], retailers: ["DIS"], retailersNote: "Curaden on Dis-Chem only" },
]

// The admin side of the rail, in four themed groups (TASK-admin-panel-revamp):
// each renders as its own NavGroup and disappears entirely when the account
// can see none of its items.
// camPage: masters always see those items; a CAM sees them when granted that
// page (the scoped-admin capabilities from TASK-user-creation-rework — the API
// side is requireAdminScoped, which also scopes what they can touch).
const PEOPLE_ACCESS = [
  // Organisations + supplier/CAM logos live inside the Users page's views now.
  { to: "/users", label: "Users", icon: FaUsers, page: "Users" },
]

const DATA_ADMIN = [
  { to: "/articles", label: "Articles", icon: FaBoxOpen, page: "Articles" },
  { to: "/stores", label: "Stores", icon: FaStore, page: "Stores" },
  { to: "/imports", label: "Imports", icon: FaFileUpload, page: "Imports" },
  { to: "/delete", label: "Delete", icon: FaTrashAlt, page: "Delete" },
  { to: "/data-status", label: "Data Status", icon: FaClipboardCheck, adminOnly: true, camPage: "DataStatus", orgPage: "DataStatus" },
  { to: "/validation", label: "Validation", icon: FaCheckDouble, adminOnly: true, camPage: "Validation" },
]

const EDI_AUTOMATION = [
  { to: "/edi", label: "EDI", icon: FaExchangeAlt, page: "Edi" },
  { to: "/pnp-ftp", label: "PnP FTP", icon: FaServer, adminOnly: true },
  // orgPage: an org admin sees it when granted that page (2026-09-17) — the API
  // side is the same requireAdminScoped gate, scoped to the organisation.
  // Alerts (2026-10-02): masters + the Reworks / Blue Mango org admins only — never a CAM.
  // orgSoon (2026-10-07): shown greyed, not clickable, for these orgs' admins — Blue
  // Mango's alert is not active yet (the server refuses it too: constants/pages.js).
  { to: "/alerts", label: "Alerts", icon: FaBell, adminOnly: true, orgPage: "Alerts", orgCodes: ["REWORKS", "BLUE_MANGO"], orgSoon: ["BLUE_MANGO"], orgSoonNote: "not active yet" },
]

const REPORTS_ADMIN = [
  // The org admins' report sender — same "Batch Reports" name as the master
  // item by request. orgOnly gates it to org_admin accounts ONLY (masters have
  // their own batch reports below, and a CAM granted 'Reports' must not see
  // this one — it drives /org-admin/* endpoints).
  { to: "/org-reports", label: "Batch Reports", icon: FaFileAlt, page: "Reports", orgOnly: true },
  { to: "/batch-reports", label: "Batch Reports", icon: FaFileAlt, adminOnly: true, camPage: "Reports" },
]

const ACCOUNT = [
  // spearOnly: accounts belonging to a partner organisation never get My
  // Profile (it carries billing/subscription/password concerns Spear manages
  // for them) — their org admin manages their account instead.
  { to: "/myProfile", label: "My Profile", icon: FaUser, page: "MyProfile", spearOnly: true },
  { to: "/loginhistory", label: "History", icon: FaHistory, page: "LoginHistory" },
]

// A production build (the prod API host) — prodSoon items show greyed there. QA and local builds are not prod.
// spearbi.org since 2026-10-15 (meterminds.co.za lapsed); the old host still counts so an older build behaves the same.
const IS_PROD_BUILD = /\/\/(spearbi\.org|spearbi\.meterminds\.co\.za)\//.test(String(process.env.REACT_APP_API_URL || ""))

const NavItem = ({ item, isActive, collapsed }) => {
  // Not available for the selected retailer: shown greyed, not a link, with the reason on hover.
  if (item.disabledNote) {
    return (
      <Tooltip label={`${item.label} — ${item.disabledNote}`} placement="right" openDelay={200}>
        <Flex
          align="center"
          gap={2.5}
          h="34px"
          px={collapsed ? 0 : 2.5}
          justify={collapsed ? "center" : "flex-start"}
          borderRadius={radius.sm}
          color="rgba(255,255,255,0.35)"
          fontSize={type.size.small}
          cursor="not-allowed"
          aria-disabled="true"
        >
          <Icon as={item.icon} boxSize="14px" flexShrink={0} opacity={0.6} />
          {!collapsed && <Text whiteSpace="nowrap">{item.label}</Text>}
        </Flex>
      </Tooltip>
    )
  }
  const content = (
    <Flex
      as={NavLink}
      to={item.to}
      align="center"
      gap={2.5}
      h="34px"
      px={collapsed ? 0 : 2.5}
      justify={collapsed ? "center" : "flex-start"}
      borderRadius={radius.sm}
      position="relative"
      // 0.78 rather than 0.66: the rail lightens toward its foot, and at 0.66
      // the last few labels fell under 4.5:1 against it.
      color={isActive ? neutral[0] : "rgba(255,255,255,0.78)"}
      bg={isActive ? "rgba(255,255,255,0.12)" : "transparent"}
      fontSize={type.size.small}
      fontWeight={isActive ? type.weight.semibold : type.weight.regular}
      transition={`background-color ${motion.fast}, color ${motion.fast}`}
      _hover={{ bg: "rgba(255,255,255,0.09)", color: neutral[0] }}
      _focusVisible={{ outline: `2px solid rgba(255,255,255,0.7)`, outlineOffset: "-2px" }}
    >
      {/* Active marker reads as a tab indicator rather than a filled pill. */}
      {isActive && (
        <Box
          position="absolute"
          left={collapsed ? "6px" : "-8px"}
          top="7px"
          bottom="7px"
          w="3px"
          borderRadius="2px"
          bg={brand[300]}
        />
      )}
      <Icon as={item.icon} boxSize="14px" flexShrink={0} opacity={isActive ? 1 : 0.85} />
      {!collapsed && <Text whiteSpace="nowrap">{item.label}</Text>}
    </Flex>
  )

  if (!collapsed) return content
  return (
    <Tooltip label={item.label} placement="right" openDelay={200}>
      <Box>{content}</Box>
    </Tooltip>
  )
}

const NavGroup = ({ title, items, pathname, collapsed }) => {
  if (items.length === 0) return null
  return (
    <Box mb={4}>
      {!collapsed && (
        <Text
          px={2.5}
          mb={1.5}
          fontSize={type.size.micro}
          fontWeight={type.weight.semibold}
          letterSpacing={type.tracking.label}
          textTransform="uppercase"
          color="rgba(255,255,255,0.42)"
        >
          {title}
        </Text>
      )}
      <Flex direction="column" gap="1px" pl={collapsed ? 0 : 2}>
        {items.map((item) => (
          <NavItem key={item.to} item={item} isActive={pathname === item.to} collapsed={collapsed} />
        ))}
      </Flex>
    </Box>
  )
}

// Loads a partner / org mark with CORS, trims the dead margin baked into the
// file and reads its ground (tools/markTrim.js). Resolves to
//   { src, dataUrl, plate }   — draw dataUrl; plate says whether it needs a light panel
//   { src, raw: true }        — pixels unreadable (no CORS on that host): draw src as it is
// and calls onFail when the URL is not an image at all, which is how the rail
// walks its candidate list. One fetch per candidate: the visible <img> only
// ever shows the analysed copy, never the network URL, so the file is not
// downloaded twice.
const useAnalysedMark = (src, enabled, onFail) => {
  const [mark, setMark] = useState(null)
  useEffect(() => {
    if (!enabled || !src) return undefined
    let cancelled = false
    const probe = new window.Image()
    probe.crossOrigin = "anonymous"
    probe.decoding = "async"
    probe.onload = () => {
      if (cancelled) return
      try {
        const result = analyseMark(probe)
        setMark(result ? { src, dataUrl: result.dataUrl, plate: result.plate } : { src, raw: true })
      } catch (e) {
        setMark({ src, raw: true })
      }
    }
    probe.onerror = () => {
      if (!cancelled) onFail()
    }
    probe.src = src
    return () => {
      cancelled = true
    }
    // onFail is a fresh closure each render; the candidate index it advances is
    // read at call time, so re-running the probe for it would only refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, enabled])
  return mark && mark.src === src ? mark : null
}

// isPhone / mobileOpen (2026-10-13): under 768px the rail is a drawer — off-canvas until the top bar's menu
// button opens it (AuthenticatedLayout). Nothing else about it changes.
const Sidebar = ({ collapsed, onToggleCollapse, userPageAccess, restrictedyn, hasSuppliers, isAdmin, isPhone = false, mobileOpen = false }) => {
  const { pathname } = useLocation()
  const { repEnabled, selectedRetailer, selectedSupplier } = useSupplierRetailer()
  // Not active yet: orgSoon = for those organisations' admins; prodSoon = for everyone on a prod build.
  const withSoonState = (item) =>
    (item.orgSoon && userType === "org_admin" && item.orgSoon.includes(orgCode)) || (item.prodSoon && IS_PROD_BUILD)
      ? { ...item, disabledNote: item.orgSoonNote || "not active yet" }
      : item
  // Items limited to some retailers (Strike Rate = PnP) grey out for the others.
  const withRetailerState = (item) =>
    (item.retailers && selectedRetailer && !item.retailers.includes(String(selectedRetailer).toUpperCase())) ||
    (item.suppliers && !item.suppliers.includes(String(selectedSupplier || "").trim().toUpperCase()))
      ? { ...item, disabledNote: item.retailersNote || "not available for this retailer" }
      : item

  // Resolved from the signed-in account, so it is fixed for the session: the
  // rail carries the partner's mark for every supplier they can select, not
  // Spear's. Read on each render rather than held in state — sign-in and
  // sign-out both remount this tree, and there is no other moment it changes.
  //
  // An org-branded mark (resolved.candidates) is tried source-by-source; when
  // no org logo exists anywhere the rail falls back to the legacy mark, so an
  // org without a logo looks exactly like before.
  const resolved = resolveBrandMark()
  const [orgMarkIdx, setOrgMarkIdx] = useState(0)
  const [orgMarkDead, setOrgMarkDead] = useState(false)
  const brandMark = resolved.candidates && orgMarkDead ? resolved.fallback : resolved
  const brandMarkSrc = resolved.candidates && !orgMarkDead ? resolved.candidates[orgMarkIdx] : brandMark.src
  const onBrandMarkError = () => {
    if (!resolved.candidates || orgMarkDead) return
    if (orgMarkIdx + 1 < resolved.candidates.length) setOrgMarkIdx(orgMarkIdx + 1)
    else setOrgMarkDead(true)
  }
  const analysedMark = useAnalysedMark(brandMarkSrc, brandMark.fit !== "crop", onBrandMarkError)
  // Light plate only when the asset's own ground asks for it; until the pixels
  // have been read (or could not be), trust the static hint.
  const markPlate = analysedMark && !analysedMark.raw ? analysedMark.plate : brandMark.plate
  const [markShown, setMarkShown] = useState(false)

  const [userType, orgCode] = (() => {
    try { return [localStorage.getItem("user_type"), localStorage.getItem("user_org_code")] } catch (e) { return [null, null] }
  })()

  // A field-only login (every grant a Field page — CURADEN, a Blue Mango rep; 2026-10-06) sees only its own
  // Field items: no Rep / Rep tracker (organisation pages), nothing else.
  const FIELD_ONLY_PAGES = ["Scorecard", "RepRoute"]
  const fieldOnly = !isAdmin && userPageAccess.length > 0 && userPageAccess.every((p) => FIELD_ONLY_PAGES.includes(p.value))
  const canSee = (item) => {
    if (item.anyOf) return item.anyOf.some((rule) => canSee(rule))
    const pageGranted = (page) => userPageAccess.some((p) => p.value === page)
    if (fieldOnly) return !!item.page && FIELD_ONLY_PAGES.includes(item.page) && pageGranted(item.page)
    if (item.adminOnly) {
      if (isAdmin) return true
      if (userType === "CAM" && item.camPage && pageGranted(item.camPage)) return true
      if (userType === "org_admin" && item.orgSoon && item.orgSoon.includes(orgCode)) return true
      if (userType === "org_admin" && item.orgPage && pageGranted(item.orgPage) && (!item.orgCodes || item.orgCodes.includes(orgCode))) return true
      return false
    }
    if (item.mastersOnly && !isAdmin) return false
    // ...and a CAM holding the item's camPage grant (Rep Routing for Monique, 2026-10-09).
    if (item.mastersOrOrgAdmin && !(isAdmin || userType === "org_admin" || (userType === "CAM" && item.camPage && pageGranted(item.camPage)))) return false
    if (item.repPage) return repEnabled && !(item.restrictable && restrictedyn)
    if (item.masterAlso && isAdmin) return true
    if (item.orgOnly && userType !== "org_admin") return false
    if (item.spearOnly && orgCode && orgCode !== "SPEARMGMT") return false
    if (item.restrictable && restrictedyn) return false
    return pageGranted(item.page)
  }

  const width = collapsed ? layout.sidebarWidthCollapsed : layout.sidebarWidth

  return (
    <Flex
      as="nav"
      direction="column"
      position="fixed"
      top={0}
      left={0}
      h="100vh"
      w={width}
      zIndex={1000}
      color={neutral[0]}
      transition={isPhone ? `transform ${motion.slow}` : `width ${motion.slow}`}
      transform={isPhone ? (mobileOpen ? "translateX(0)" : "translateX(-105%)") : undefined}
      boxShadow={isPhone && mobileOpen ? "0 0 40px rgba(0,0,0,0.45)" : undefined}
      aria-hidden={isPhone && !mobileOpen ? true : undefined}
      overflow="hidden"
      sx={{
        // navbar_bg.svg — generated by scripts/build-nav-background.js. Keeps
        // the flowing bands and geometric line-work of the original artwork,
        // but drawn dark enough to carry white labels at AA on its own, so no
        // scrim is painted over it. Vector, so it stays crisp at any viewport
        // height: 4.8 kB against the old PNG's 1.5 MB.
        backgroundImage: `url('/navbar_bg.svg')`,
        // Stretched rather than `cover`: the rail's aspect ratio swings from
        // ~0.15 to ~0.30 depending on viewport height, and `cover` cropped the
        // composition's foot off on shorter screens. The art is soft bands and
        // low-opacity line-work, so the slight non-uniform scale is invisible —
        // and the top crest and foot motif are then always both in frame.
        backgroundSize: "100% 100%",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: brand[800],
      }}
    >
      <Box position="relative" flexShrink={0} borderBottom="1px solid rgba(255,255,255,0.12)">
        {/* The mark is Spear's unless the signed-in account is a white-label
            partner, in which case every screen they see carries theirs instead
            — see tools/brandMark.js. The two draw differently and must not share
            a code path: the Spear asset needs a crop, a partner asset does not. */}
        {brandMark.fit === "crop" ? (
          /* spearbi_ai4.png is the transparent script wordmark — the same file
             the landing navbar uses. It is a 500×500 square whose glyph occupies
             only the middle ~58% × ~48%, so drawing it with object-fit leaves it
             looking half-size. Oversizing it and letting the box clip trims the
             dead padding without needing a second cropped asset.
             (The rail previously loaded spearbi_ai1.jpg: 30123×25472px.) */
          <Box
            role="img"
            aria-label={brandMark.label}
            h={collapsed ? "48px" : "90px"}
            overflow="hidden"
            transition={`height ${motion.slow}`}
            filter="drop-shadow(0 2px 8px rgba(0,0,0,0.35))"
            sx={{
              backgroundImage: `url('${brandMark.src}')`,
              backgroundRepeat: "no-repeat",
              // 42% rather than centre: the glyph sits above the middle of the
              // source square, so a straight centre crop reads top-heavy.
              backgroundPosition: "center 42%",
              // Scaled per state: the glyph is ~58% of the square's width, so the
              // 64px collapsed rail needs a smaller multiplier or the wordmark
              // runs off both edges.
              backgroundSize: collapsed ? "auto 150%" : "auto 195%",
            }}
          />
        ) : (
          /* A partner asset comes on an opaque ground of its own — white for
             Gateway's JPEG, the rail's teal for Blue Mango's PNG — and with dead
             margin baked into the file. It is trimmed to its content on the way
             in (useAnalysedMark) and the image IS the card: no padding around it
             (a white plate with an 8px inset used to frame every mark in a white
             border), corners rounded on the image itself, a hairline ring so a
             mark whose ground is close to the rail's still has an edge, and a
             light plate only under a light-ground asset. It fills the header's
             inner height, sits on the same left gutter as the supplier pill
             below, and leaves the top-right corner to the collapse toggle;
             collapsed, the toggle drops beneath it instead (see the IconButton),
             so the two never overlap. */
          <Flex
            align="center"
            justify={collapsed ? "center" : "flex-start"}
            h={collapsed ? "48px" : "90px"}
            pl={collapsed ? 1.5 : 3}
            pr={collapsed ? 1.5 : 10}
            py={collapsed ? 1.5 : 2}
            transition={`height ${motion.slow}`}
          >
            {analysedMark && (
              <Image
                key={brandMarkSrc}
                src={analysedMark.dataUrl || analysedMark.src}
                onError={analysedMark.raw ? onBrandMarkError : undefined}
                onLoad={() => setMarkShown(true)}
                alt={brandMark.label}
                title={brandMark.label}
                maxH="100%"
                maxW="100%"
                objectFit="contain"
                bg={markPlate ? neutral[0] : "transparent"}
                borderRadius={radius.sm}
                boxShadow="0 0 0 1px rgba(255,255,255,0.14), 0 2px 8px rgba(0,0,0,0.35)"
                opacity={markShown ? 1 : 0}
                transition={`opacity ${motion.base}`}
              />
            )}
          </Flex>
        )}

        <IconButton
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          icon={collapsed ? <FaAngleDoubleRight /> : <FaAngleDoubleLeft />}
          onClick={onToggleCollapse}
          variant="ghost"
          size="xs"
          fontSize="11px"
          /* Expanded: the header's top-right corner, which the mark leaves free.
             Collapsed: a 64px rail has no free corner beside a 52px mark, so the
             toggle takes a short centred row of its own under it. */
          position={collapsed ? "relative" : "absolute"}
          top={collapsed ? undefined : "6px"}
          right={collapsed ? undefined : "6px"}
          display={isPhone ? "none" : "flex"}
          mx={collapsed ? "auto" : undefined}
          mb={collapsed ? 1 : undefined}
          color="rgba(255,255,255,0.55)"
          _hover={{ bg: "rgba(255,255,255,0.16)", color: neutral[0] }}
          _focusVisible={{ outline: "2px solid rgba(255,255,255,0.7)", outlineOffset: "-2px" }}
        />
      </Box>

      {hasSuppliers && (
        <Box px={collapsed ? 1.5 : 3} py={3} flexShrink={0}>
          <SupplierNavigation collapsed={collapsed} />
        </Box>
      )}

      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        px={collapsed ? 1.5 : 3}
        pb={4}
        css={{
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: "3px" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255,255,255,0.22)",
            borderRadius: "3px",
            border: "none",
          },
          "&::-webkit-scrollbar-track": { background: "transparent" },
        }}
      >
        <NavGroup
          title="Analytics"
          items={ANALYTICS.filter(canSee).map(withRetailerState)}
          pathname={pathname}
          collapsed={collapsed}
        />
        <NavGroup
          title="Reps"
          items={FIELD.filter(canSee).map(withRetailerState).map(withSoonState)}
          pathname={pathname}
          collapsed={collapsed}
        />
        <NavGroup
          title="People & Access"
          items={PEOPLE_ACCESS.filter(canSee)}
          pathname={pathname}
          collapsed={collapsed}
        />
        <NavGroup
          title="Data"
          items={DATA_ADMIN.filter(canSee)}
          pathname={pathname}
          collapsed={collapsed}
        />
        <NavGroup
          title="EDI & Automation"
          items={EDI_AUTOMATION.filter(canSee).map((item) => (userType === "org_admin" && item.orgSoon && item.orgSoon.includes(orgCode) ? { ...item, disabledNote: item.orgSoonNote || "not active yet" } : item))}
          pathname={pathname}
          collapsed={collapsed}
        />
        <NavGroup
          title="Reports"
          items={REPORTS_ADMIN.filter(canSee)}
          pathname={pathname}
          collapsed={collapsed}
        />
        <NavGroup
          title="Account"
          items={ACCOUNT.filter(canSee)}
          pathname={pathname}
          collapsed={collapsed}
        />
      </Box>
    </Flex>
  )
}

export default Sidebar
